import { RiCloseLine, RiPencilLine, RiSaveLine } from '@remixicon/react';
import { PropsWithChildren, useEffect, useState } from 'react';
import { SlugValidator } from 'shared/validators';
import PageHeaderLabels from 'src/modules/labels/PageHeaderLabels';
import { GLOBAL_APP_BAR_HEIGHT } from '../../modules/pageFrame/SiteHeader';
import Breadcrumbs, { BreadcrumbType } from '../atoms/Breadcrumbs';
import { AgButton, AgInput, AgText } from '../ui';
import { cn } from '../ui/utils';

export enum PageType {
  // Accounting
  AccountReconciliation,
  AccountReconciliations,
  AccountRegister,
  Accounts,

  Invoice,
  Invoices,

  Payments,
  Receipt,
  Disbursement,

  Payable,
  Payables,

  Settlement,
  PrintChecks,

  // Business Intelligence
  AgingReports,
  AccountingReports,
  BusinessIntelligence,

  // Inventory
  Inventory,
  Lot,

  InventoryTransfer,
  InventoryTransfers,

  ProductionRun,
  ProductionRuns,

  // Freight Tracking
  FreightTracking,
  Load,

  // Order Management system
  PurchaseOrder,
  PurchaseOrders,

  ReceivingTicket,
  ReceivingTickets,

  SalesOrder,
  SalesOrders,

  PickTicket,
  PickTickets,

  GrowerProduct,
  GrowerProducts,

  BuySellOrder,
  BuySellOrders,

  OrderBoard,

  // System Settings
  Settings,
  AccountingSettings,
  AccountMappings,
  BusinessSettings,
  AutomationSettings,
  EmailSettings,
  OrderSettings,
  DocumentSettings,
  InventorySettings,
  WarehouseDetails,
  TeamSettings,

  BusinessEntityManagement,
  Products,
  PricingSheet,
}

interface PageTitleMapping {
  title: string;
  compactTitle: string;
}

const PageTypeToTitleMapping: Record<PageType, PageTitleMapping> = {
  // -- Accounting
  [PageType.AccountReconciliation]: { title: 'Account Reconciliation', compactTitle: 'Account Reconciliation' },
  [PageType.AccountReconciliations]: { title: 'Account Reconciliations', compactTitle: 'Account Reconciliations' },
  [PageType.AccountRegister]: { title: 'Account Register', compactTitle: 'Account Register' },
  [PageType.Accounts]: { title: 'Accounts', compactTitle: 'Accounts' },

  [PageType.Invoice]: { title: 'Invoice', compactTitle: 'Invoice' },
  [PageType.Invoices]: { title: 'Invoices', compactTitle: 'Invoices' },

  [PageType.Payments]: { title: 'Payments', compactTitle: 'Payments' },
  [PageType.Receipt]: { title: 'Receipt', compactTitle: 'Receipt' },
  [PageType.Disbursement]: { title: 'Disbursement', compactTitle: 'Disbursement' },

  [PageType.Payable]: { title: 'Payable', compactTitle: 'Payable' },
  [PageType.Payables]: { title: 'Payables', compactTitle: 'Payables' },
  [PageType.Settlement]: { title: 'Settlement', compactTitle: 'Settlement' },
  [PageType.PrintChecks]: { title: 'Print Checks', compactTitle: 'Print Checks' },

  // -- Business Intelligence
  [PageType.AgingReports]: { title: 'Aging Reports', compactTitle: 'Aging Reports' },
  [PageType.AccountingReports]: { title: 'Accounting Reports', compactTitle: 'Accounting Reports' },
  [PageType.BusinessIntelligence]: { title: 'Business Intelligence', compactTitle: 'Business Intelligence' },

  // Inventory
  [PageType.Inventory]: { title: 'Inventory Report', compactTitle: 'Inventory' },
  [PageType.Lot]: { title: 'Lot', compactTitle: 'Lot' },

  [PageType.InventoryTransfer]: { title: 'Inventory Transfer', compactTitle: 'IT' },
  [PageType.InventoryTransfers]: { title: 'Inventory Transfers', compactTitle: 'Inventory Transfers' },

  [PageType.ProductionRun]: { title: 'Production Run', compactTitle: 'PR' },

  [PageType.ProductionRuns]: { title: 'Production Runs', compactTitle: 'Production Runs' },

  // -- Freight Tracking
  [PageType.FreightTracking]: { title: 'Freight Tracking', compactTitle: 'Freight Tracking' },

  [PageType.Load]: { title: 'Load', compactTitle: 'Load' },

  // -- Order Management system
  [PageType.PurchaseOrder]: { title: 'Purchase Order', compactTitle: 'PO' },
  [PageType.PurchaseOrders]: { title: 'Purchase Orders', compactTitle: 'Purchase Orders' },

  [PageType.ReceivingTicket]: { title: 'Receiving Ticket', compactTitle: 'RT' },
  [PageType.ReceivingTickets]: { title: 'Receiving Tickets', compactTitle: 'Receiving Tickets' },

  [PageType.SalesOrder]: { title: 'Sales Order', compactTitle: 'SO' },
  [PageType.SalesOrders]: { title: 'Sales Orders', compactTitle: 'Sales Orders' },

  [PageType.PickTicket]: { title: 'Pick Ticket', compactTitle: 'Pick Ticket' },
  [PageType.PickTickets]: { title: 'Pick Tickets', compactTitle: 'Pick Tickets' },

  [PageType.GrowerProduct]: { title: 'Grower Product', compactTitle: 'GP' },
  [PageType.GrowerProducts]: { title: 'Grower Products', compactTitle: 'Grower Products' },

  [PageType.BuySellOrder]: { title: 'Buy-Sell Order', compactTitle: 'BSO' },
  [PageType.BuySellOrders]: { title: 'Buy-Sell Orders', compactTitle: 'Buy-Sell Orders' },

  [PageType.OrderBoard]: { title: 'Order Board', compactTitle: 'Order Board' },

  // -- System Settings
  [PageType.Settings]: { title: 'Settings', compactTitle: 'Settings' },
  [PageType.AccountingSettings]: { title: 'Accounting Settings', compactTitle: 'Accounting Settings' },
  [PageType.AccountMappings]: { title: 'Account Mappings', compactTitle: 'Account Mappings' },
  [PageType.BusinessSettings]: { title: 'Business Setup', compactTitle: 'Business Setup' },
  [PageType.AutomationSettings]: { title: 'Automation Settings', compactTitle: 'Automation Settings' },
  [PageType.EmailSettings]: { title: 'Email Settings', compactTitle: 'Email Settings' },
  [PageType.OrderSettings]: { title: 'Order Settings', compactTitle: 'Order Settings' },
  [PageType.DocumentSettings]: { title: 'Document Settings', compactTitle: 'Document Settings' },
  [PageType.InventorySettings]: {
    title: 'Inventory Management Settings',
    compactTitle: 'Inventory Management Settings',
  },
  [PageType.WarehouseDetails]: { title: 'Warehouse Details', compactTitle: 'Warehouse Details' },
  [PageType.TeamSettings]: { title: 'Team Settings', compactTitle: 'Team Settings' },

  // To organize

  [PageType.BusinessEntityManagement]: {
    title: 'Business Entity Management',
    compactTitle: 'Business Entity Management',
  },
  [PageType.Products]: { title: 'Products', compactTitle: 'Products' },
  [PageType.PricingSheet]: { title: 'Pricing Sheet', compactTitle: 'Pricing Sheet' },
};

export type PageHeaderProps = {
  /**
   * The type of page that is being rendered.
   */
  pageType: PageType;

  /**
   * The slug of the entity that is being rendered.
   */
  entitySlug?: string;

  /**
   * Delimiter to join Page Type and Entity Slug
   */
  delimiter?: string;

  /**
   * Custom page title to render, overrides pageType et al
   */
  title?: string;

  /**
   * Sometimes the Slug of the page is editable. This function is called when the edited title is to be saved in the backend.
   * @param slug New slug
   * @returns
   */
  entitySlugEditHandler?: ((slug: string) => Promise<string>) | null;

  /**
   * Optional test id for the entity slug edit input (used by callers like PO header).
   */
  entitySlugEditTestId?: string;

  /**
   * Optional test id forwarded to the "+" add-label button rendered by `PageHeaderLabels`.
   */
  addLabelTestId?: string;

  children?: PropsWithChildren<React.ReactNode>;
  error?: boolean | null;
  info?: boolean;
  labels?: boolean;
  hydrateLabels?: boolean;
  afterTitle?: React.ReactNode;
  below?: React.ReactNode;
  /**
   * Override the default breadcrumbs handler.
   */
  getBreadcrumbs?: (paths: string[]) => BreadcrumbType[];
};

export function PageHeader({
  pageType,
  entitySlug,
  delimiter,
  title,

  children,
  error,
  info,
  labels,
  hydrateLabels = true,
  entitySlugEditHandler = null,
  entitySlugEditTestId,
  addLabelTestId,
  below = null,
  getBreadcrumbs,
}: PageHeaderProps) {
  const [editedPageTitle, setEditedPageTitle] = React.useState<string | null>(null);
  const [editedCompactPageTitle, setEditedCompactPageTitle] = React.useState<string | null>(null);

  const pageTitle =
    editedPageTitle ||
    title ||
    (entitySlug && `${PageTypeToTitleMapping[pageType].title} ${delimiter ?? '#'}${entitySlug}`) ||
    PageTypeToTitleMapping[pageType].title;

  const compactPageTitle =
    editedCompactPageTitle ||
    title ||
    (entitySlug && `${PageTypeToTitleMapping[pageType].compactTitle} ${delimiter ?? '#'}${entitySlug}`) ||
    PageTypeToTitleMapping[pageType].compactTitle;

  const [editMode, setEditMode] = React.useState(false);
  const [editedSlug, setEditedSlug] = React.useState(entitySlug);
  const [editedSlugError, setEditedSlugError] = React.useState<string | null>(null);

  const handleEditSlugEnterIntent = () => {
    setEditMode(true);
  };

  const handleEditSlugSaveIntent = () => {
    if (editedSlug === undefined) {
      throw new TypeError("Cannot read properties of undefined (reading 'length')");
    }

    const error = SlugValidator.validateSlugFormat(editedSlug);
    if (error) {
      setEditedSlugError(error);
      return; // Do not proceed to save
    } else {
      setEditedSlugError(null);
    }

    if (typeof entitySlugEditHandler !== 'function') {
      throw new TypeError('entitySlugEditHandler is not a function');
    }

    entitySlugEditHandler(editedSlug)
      .then((savedSlug) => {
        // Update `slug` in the URL
        setEditedPageTitle(`${PageTypeToTitleMapping[pageType].title} ${savedSlug}`);
        setEditedCompactPageTitle(`${PageTypeToTitleMapping[pageType].compactTitle} ${savedSlug}`);

        setEditMode(false);
      })
      .catch((err) => {
        console.error('Slug edit failed', err);
        setEditedSlugError(err.message);
      });
  };

  const handleEditSlugCancelIntent = () => {
    setEditMode(false);
  };

  // Progressive scroll-linked collapse: the header's padding/gap interpolates directly from scroll position, and a sibling spacer grows by the same amount so total document height stays constant.
  // Constant height is what prevents the jumpy feedback loop on marginally-scrollable pages — the browser never clamps scroll back down, so the state cannot oscillate.
  const SHRINK_DISTANCE = 40;
  const EXPANDED_PY = 24;
  const COLLAPSED_PY = 12;
  const EXPANDED_GAP = 16;
  const COLLAPSED_GAP = 8;

  const [scrollY, setScrollY] = useState(() => (typeof window !== 'undefined' ? window.scrollY : 0));
  useEffect(() => {
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        setScrollY(window.scrollY);
        rafId = null;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const progress = Math.min(1, Math.max(0, scrollY / SHRINK_DISTANCE));
  const isScrolled = progress > 0;
  const paddingY = EXPANDED_PY - (EXPANDED_PY - COLLAPSED_PY) * progress;
  const gap = EXPANDED_GAP - (EXPANDED_GAP - COLLAPSED_GAP) * progress;
  const spacerHeight = SHRINK_DISTANCE * progress;

  return (
    <>
      <div
        className={cn(
          'col w-full sticky z-40 px-6 transition-colors duration-200 border-neutral-5 overscroll-y-none',
          isScrolled ? 'bg-white shadow-sm border-b' : 'bg-transparent',
          info && 'text-blue-text',
          error && 'text-red-text'
        )}
        style={{ top: GLOBAL_APP_BAR_HEIGHT, paddingTop: paddingY, paddingBottom: paddingY, gap }}
      >
        {labels ? (
          <div className="flex w-full">
            <Breadcrumbs getBreadcrumbs={getBreadcrumbs} />
            <div className="flex-1" />
            <PageHeaderLabels addLabelTestId={addLabelTestId} hydrateLabels={hydrateLabels} />
          </div>
        ) : (
          <Breadcrumbs getBreadcrumbs={getBreadcrumbs} />
        )}

        <div className="flex items-center gap-1.5">
          {editMode && entitySlugEditHandler ? (
            // Edit the title
            <div className="flex items-center gap-1.5">
              <AgInput
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEditSlugSaveIntent();
                    e.preventDefault();
                  }
                }}
                value={editedSlug}
                onChange={(value) => setEditedSlug(value?.toUpperCase())}
                size="sm"
                inputClassName="uppercase"
                errorMessage={editedSlugError ?? undefined}
                data-testid={entitySlugEditTestId}
              />

              <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={handleEditSlugSaveIntent}>
                <RiSaveLine size={20} />
              </AgButton>

              <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={handleEditSlugCancelIntent}>
                <RiCloseLine size={20} />
              </AgButton>
            </div>
          ) : (
            // Show the title
            <div className="flex items-center">
              <AgText
                size="h6"
                weight="semibold"
                color="inherit"
                className="text-nowrap text-ellipsis md:hidden"
                id="page-title"
              >
                {compactPageTitle}
              </AgText>
              <AgText
                size="h6"
                weight="semibold"
                color="inherit"
                className="text-nowrap text-ellipsis hidden md:inline"
                id="page-title"
              >
                {pageTitle}
              </AgText>

              {entitySlugEditHandler && (
                <AgButton variant="ghost" size="icon-sm" shape="circle" onPress={handleEditSlugEnterIntent}>
                  <RiPencilLine />
                </AgButton>
              )}
            </div>
          )}

          {children}
        </div>
        {below}
      </div>
      <div aria-hidden style={{ height: spacerHeight }} />
    </>
  );
}
